/* DoorGlow product page: <dg-product> (options, price, fitment, add to cart, sticky bar)
 * and <dg-gallery> (swipe, thumbnails, zoom). Depends on doorglow-core.js (DoorGlow)
 * and doorglow-cart.js (DoorGlowCart, ES module — falls back to a normal form post).
 */
(() => {
  const core = () => window.DoorGlow;
  const whenCore = () => (core() ? Promise.resolve() : new Promise((r) => document.addEventListener('DOMContentLoaded', r, { once: true })));
  const whenCart = () => (window.DoorGlowCart ? Promise.resolve(window.DoorGlowCart)
    : new Promise((r) => { document.addEventListener('doorglow:cart-ready', () => r(window.DoorGlowCart), { once: true }); setTimeout(() => r(window.DoorGlowCart || null), 2500); }));

  /* ======================= Gallery ======================= */
  class DoorGlowGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-dg-track]');
      this.slides = [...this.querySelectorAll('.dg-gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-dg-thumb]')];
      this.counter = this.querySelector('[data-dg-current]');
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => this.onScroll()); }, { passive: true });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.dgThumb))));
      this.querySelector('[data-dg-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-dg-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
      });
      this.setupZoom();
    }
    onScroll() {
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) this.setIndex(i);
    }
    setIndex(i) {
      this.index = i;
      if (this.counter) this.counter.textContent = String(i + 1);
      this.thumbs.forEach((t, n) => t.toggleAttribute('aria-current', n === i));
      this.thumbs[i]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    goTo(i, smooth = true) {
      if (!this.slides.length) return;
      const n = (i + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: n * this.track.clientWidth, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
      this.setIndex(n);
    }
    showMedia(mediaId) {
      if (!mediaId) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1 && i !== this.index) this.goTo(i);
    }
    setupZoom() {
      const dlg = this.querySelector('[data-dg-zoom-dialog]');
      if (!dlg) return;
      const img = dlg.querySelector('[data-dg-zoom-img]');
      if (!img) return; // product without media: nothing to zoom
      const stage = dlg.querySelector('[data-dg-zoom-stage]');
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-dg-zoom]');
        if (!btn) return;
        const src = btn.dataset.zoomSrc;
        img.src = src;
        img.alt = btn.querySelector('img')?.alt || '';
        stage.classList.remove('is-zoomed');
        dlg.showModal();
      });
      // Tap/click toggles 2× zoom centred on the tap point; drag/scroll to pan.
      stage.addEventListener('click', (e) => {
        const r = img.getBoundingClientRect();
        const fx = (e.clientX - r.left) / r.width, fy = (e.clientY - r.top) / r.height;
        stage.classList.toggle('is-zoomed');
        if (stage.classList.contains('is-zoomed')) {
          requestAnimationFrame(() => stage.scrollTo({ left: fx * stage.scrollWidth - stage.clientWidth / 2, top: fy * stage.scrollHeight - stage.clientHeight / 2 }));
        }
      });
      dlg.querySelector('[data-dg-zoom-close]').addEventListener('click', () => dlg.close());
      dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    }
  }
  customElements.get('dg-gallery') || customElements.define('dg-gallery', DoorGlowGallery);

  /* ======================= Product ======================= */
  class DoorGlowProduct extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-dg-product-json]').textContent);
      this.form = this.querySelector('[data-dg-form]');
      this.idInput = this.querySelector('[data-dg-variant-input]');
      this.vehicleInput = this.querySelector('[data-dg-vehicle-input]');
      this.atc = this.querySelector('[data-dg-atc]');
      this.gallery = this.querySelector('dg-gallery');
      this.variant = this.data.variants.find((v) => v.id === this.data.currentVariant) || this.data.variants[0];
      this.selected = [...this.variant.options];
      this.qtyValues = this.data.qtyIndex === null ? [] : [...new Set(this.data.variants.map((v) => v.options[this.data.qtyIndex]))];

      this.querySelectorAll('[data-dg-option]').forEach((fs) => {
        fs.addEventListener('change', (e) => {
          if (e.target.type !== 'radio') return;
          this.userPickedQty ||= Number(fs.dataset.dgOption) === this.data.qtyIndex;
          this.selected[Number(fs.dataset.dgOption)] = e.target.value;
          this.update();
        });
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.querySelector('[data-dg-sticky-atc]')?.addEventListener('click', () => this.form.requestSubmit(this.atc));
      this.querySelectorAll('[data-dg-yt]').forEach((el) => el.addEventListener('click', () => {
        el.innerHTML = `<iframe src="https://www.youtube-nocookie.com/embed/${encodeURIComponent(el.dataset.dgYt)}?autoplay=1&rel=0" title="Installation video" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>`;
      }, { once: true }));
      this.setupSticky();
      this.update(false);
      whenCore().then(() => {
        this.evaluateFit(core().vehicle.get());
        this.unsub = core().vehicle.subscribe((v) => this.evaluateFit(v));
      });
    }
    disconnectedCallback() { this.unsub?.(); this.observer?.disconnect(); }

    findVariant(options) { return this.data.variants.find((v) => v.options.every((o, i) => o === options[i])); }
    money(c) { return core() ? core().money(c) : `$${(c / 100).toFixed(2)}`; }
    moneyShort(c) { return core() ? core().moneyShort(c) : `$${Math.round(c / 100)}`; }

    /* ---------- options → variant ---------- */
    update(pushUrl = true) {
      const v = this.findVariant(this.selected);
      const { designIndex, qtyIndex } = this.data;
      this.querySelectorAll('[data-dg-option]').forEach((fs) => {
        const label = fs.querySelector('[data-dg-option-label]');
        if (label) label.textContent = this.selected[Number(fs.dataset.dgOption)];
      });
      const ph = this.querySelector('[data-dg-placeholder-design]');
      if (ph) ph.textContent = this.selected[designIndex];

      // Bundle maths for the selected design: 2 × (2-light) − (4-light)
      if (qtyIndex !== null && this.qtyValues.length > 1) {
        const byQty = this.qtyValues.map((q) => { const o = [...this.selected]; o[qtyIndex] = q; return this.findVariant(o); });
        const [small, large] = [byQty[0], byQty[byQty.length - 1]];
        const saving = small && large ? small.price * 2 - large.price : 0;
        const hint = this.querySelector('[data-dg-bundle-hint]');
        const tpl = core()?.settings.bundleHint || 'Save [amount] with 4 lights';
        if (hint) {
          hint.hidden = !(saving > 0) || this.selected[qtyIndex] === this.qtyValues[this.qtyValues.length - 1];
          hint.querySelector('span').textContent = tpl.replace('[amount]', this.moneyShort(saving));
        }
        this.querySelectorAll('[data-dg-option="' + qtyIndex + '"] .dg-qty__card').forEach((card, i) => {
          const variant = byQty[i];
          card.querySelector('[data-dg-qty-price]').textContent = variant ? this.moneyShort(variant.price) : '';
          const save = card.querySelector('[data-dg-qty-save]');
          if (save) { save.hidden = !(i === byQty.length - 1 && saving > 0); save.textContent = `Save ${this.moneyShort(saving)}`; }
        });
      }

      if (!v) {
        this.setAtc(false, 'Unavailable');
        return;
      }
      this.variant = v;
      this.idInput.value = v.id;
      this.querySelector('[data-dg-price]').textContent = this.money(v.price);
      const onSale = v.compare > v.price; // only real compare-at prices set in admin
      this.querySelector('[data-dg-compare]').hidden = !onSale;
      if (onSale) this.querySelector('[data-dg-compare]').textContent = this.money(v.compare);
      this.querySelector('[data-dg-sale-badge]').hidden = !onSale;
      this.setAtc(v.available, v.available ? null : 'Sold out');
      // On first load keep the gallery on the lead photo (a night shot); jump to the
      // design's image once the buyer picks one, or when a link names a variant.
      if (pushUrl || new URLSearchParams(location.search).has('variant')) this.gallery?.showMedia(v.media);
      const sv = this.querySelector('[data-dg-sticky-variant]');
      if (sv) sv.textContent = v.title;
      const sp = this.querySelector('[data-dg-sticky-price]');
      if (sp) sp.textContent = this.money(v.price);
      if (pushUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState(history.state, '', url);
      }
    }
    get atcLabel() { return this.dataset.atcLabel || 'Add to cart'; }
    setAtc(enabled, text) {
      this.atc.disabled = !enabled;
      this.atc.querySelector('[data-dg-atc-label]').textContent = text || this.atcLabel;
      const s = this.querySelector('[data-dg-sticky-atc]');
      if (s) { s.disabled = !enabled; s.textContent = text || this.atcLabel; }
    }

    /* ---------- fitment ---------- */
    async evaluateFit(v) {
      const box = this.querySelector('[data-dg-fit]');
      if (!box) return;
      const handles = (box.dataset.fitHandles || '').split(',').filter(Boolean);
      const show = (state) => box.querySelectorAll('[data-state]').forEach((el) => (el.hidden = el.dataset.state !== state));
      // Brand-wide listings (no per-car list) match on make; the buyer confirms factory door lights.
      const makes = (box.dataset.universalMakes || '').split(',').filter(Boolean);
      const brandFit = !handles.length && makes.some((m) => m.toLowerCase() === (v?.make || '').toLowerCase());
      this.fitState = !v ? 'none' : core().fitment.fits(handles, v) ? 'fits' : brandFit ? 'brand' : 'nofit';
      box.querySelectorAll('[data-dg-fit-car]').forEach((el) => (el.textContent = v ? v.label : ''));
      box.querySelectorAll('[data-dg-fit-make]').forEach((el) => (el.textContent = v ? v.make : ''));
      show(this.fitState);
      box.querySelector('[data-dg-fit-summary]')?.toggleAttribute('hidden', this.fitState === 'fits' || this.fitState === 'brand');
      this.querySelector('[data-dg-car-nudge]').hidden = true;
      if (!v) this.renderChoices(box);

      // Don't put a car that doesn't fit onto the order.
      if (this.vehicleInput) {
        this.vehicleInput.toggleAttribute('data-dg-fit-blocked', this.fitState === 'nofit');
        core().syncVehicleInputs(this);
      }
      box.querySelector('[data-dg-fit-more]')?.toggleAttribute('hidden', true);
      if (!v) return;

      let feedVehicle = null;
      try { feedVehicle = (await core().feed.load()).find((x) => x.handle === v.handle); } catch { /* offline */ }
      this.renderMore(feedVehicle, v);
      if (this.fitState === 'fits' || this.fitState === 'brand') {
        const twoDoor = feedVehicle?.doors === 2;
        box.querySelector(`[data-state="${this.fitState}"] [data-dg-fit-doors]`).hidden = !twoDoor;
        if (twoDoor && !this.userPickedQty && this.data.qtyIndex !== null) this.pickQty(0);
      } else if (this.fitState === 'nofit') {
        const link = box.querySelector('[data-dg-fit-link]');
        const msg = box.querySelector('[data-dg-nofit-msg]');
        const target = feedVehicle?.products?.find((p) => p.url !== location.pathname);
        if (target) {
          link.href = `${target.url}?vehicle=${encodeURIComponent(v.handle)}`;
          link.textContent = `Shop lights for your ${v.make} ${v.chassis || v.model}`;
          msg.textContent = 'Good news — we have a set made for your car.';
        } else {
          link.href = `${(window.Shopify?.routes?.root || '/')}collections/door-puddle-lights`;
          link.textContent = 'Browse all door lights';
          msg.textContent = 'We don’t have a set for your car yet — new models are added often.';
        }
      }
    }
    /** "More designs for your M3 (G80): BMW Logo · BMW Engine Code" under the fit check. */
    /**
     * One-tap car choice when no car is saved: a button per car this listing is made for
     * (from the vehicle feed, so it works for brand-wide Audi/Mercedes listings too), or a
     * "Driving a …? Yes, that's my car" confirm when the page was opened for one car
     * (?vehicle=). Listings are per chassis, so the year adds nothing: the car is saved
     * without one ("BMW M3 (G80)"). "My car isn't listed" opens the full picker.
     */
    async renderChoices(box) {
      const wrap = box.querySelector('[data-dg-fit-choices]');
      const picker = box.querySelector('[data-dg-fit-picker]');
      if (!wrap || !picker || this.choicesBuilt) return;
      this.choicesBuilt = true;
      let feed = [];
      try { feed = await core().feed.load(); } catch { /* offline: keep the full picker */ }
      const cars = feed
        .filter((x) => (x.products || []).some((p) => p.url === this.dataset.productUrl))
        .sort((a, b) => a.model.localeCompare(b.model) || (b.yearStart || 0) - (a.yearStart || 0));
      if (!cars.length) return;

      const esc = (t) => String(t ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const years = (c) => `${c.yearStart || ''}–${c.yearEnd || 'present'}`;
      const chip = (c) => `<button type="button" class="dg-carchip" data-dg-car="${esc(c.handle)}">
          <span class="dg-carchip__name">${esc(c.model)} <span class="dg-carchip__code">${esc(c.chassis)}</span></span>
          <span class="dg-carchip__years">${esc(years(c))}</span></button>`;
      const param = new URLSearchParams(location.search).get('vehicle');
      const linked = cars.find((c) => c.handle === param);

      wrap.innerHTML = (linked
        ? `<div class="dg-fit__confirm">
             <button type="button" class="dg-btn dg-btn--block dg-fit__yes" data-dg-car="${esc(linked.handle)}">Yes, that's my car</button>
             ${cars.length > 1 ? '<button type="button" class="dg-fit__link" data-dg-fit-others>No, show the other models</button>' : ''}
           </div>
           <div class="dg-carchips" data-dg-fit-list hidden>${cars.map(chip).join('')}</div>`
        : `<div class="dg-carchips" data-dg-fit-list>${cars.map(chip).join('')}</div>`)
        + '<button type="button" class="dg-fit__link" data-dg-fit-other>My car isn’t listed</button>';
      box.querySelector('[data-dg-fit-question]').textContent = linked
        ? `Driving ${/^[AEIOU]/i.test(linked.make) ? 'an' : 'a'} ${linked.make} ${linked.model} (${linked.chassis}, ${years(linked)})?`
        : (cars.length > 1 ? 'Which one is yours?' : 'Is this your car?');
      wrap.hidden = false;
      picker.hidden = true;
      box.querySelector('[data-dg-fit-summary]')?.setAttribute('hidden', '');

      wrap.addEventListener('click', (e) => {
        const pick = e.target.closest('[data-dg-car]');
        if (pick) {
          const c = cars.find((x) => x.handle === pick.dataset.dgCar);
          if (c) core().vehicle.set({ handle: c.handle, make: c.make, model: c.model, series: c.series, chassis: c.chassis, year: null, yearStart: c.yearStart, yearEnd: c.yearEnd });
          return;
        }
        if (e.target.closest('[data-dg-fit-others]')) {
          wrap.querySelector('[data-dg-fit-list]').hidden = false;
          e.target.closest('[data-dg-fit-others]').remove();
          box.querySelector('[data-dg-fit-question]').textContent = 'Which one is yours?';
          return;
        }
        if (e.target.closest('[data-dg-fit-other]')) {
          wrap.hidden = true;
          picker.hidden = false;
          box.querySelector('[data-dg-fit-question]').textContent = 'Find your car';
          picker.querySelector('select:not([disabled])')?.focus();
        }
      });
    }
    renderMore(feedVehicle, v) {
      const el = this.querySelector('[data-dg-fit-more]');
      if (!el) return;
      const others = (feedVehicle?.products || []).filter((p) => p.url !== this.dataset.productUrl && p.available !== false);
      const show = others.length > 0 && (this.fitState === 'fits' || this.fitState === 'brand');
      el.hidden = !show;
      if (!show) return;
      const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const name = (t) => t.split(' Door Puddle Lights')[0];
      const href = (p) => `${p.url}?vehicle=${encodeURIComponent(v.handle)}`;
      el.innerHTML = `More designs for your ${esc(v.model)} (${esc(v.chassis)}): `
        + others.map((p) => `<a href="${esc(href(p))}">${esc(name(p.title))}</a>`).join(' · ');
    }
    pickQty(i) {
      const fs = this.querySelector(`[data-dg-option="${this.data.qtyIndex}"]`);
      const radio = fs?.querySelectorAll('input[type=radio]')[i];
      if (radio && !radio.checked) { radio.checked = true; this.selected[this.data.qtyIndex] = radio.value; this.update(); }
    }

    /* ---------- add to cart ---------- */
    async onSubmit(e) {
      e.preventDefault();
      const nudge = this.querySelector('[data-dg-car-nudge]');
      if (this.dataset.requireCar === 'true' && this.fitState === 'none' && !this.nudged) {
        this.nudged = true;
        nudge.hidden = false;
        const fit = this.querySelector('[data-dg-fit]');
        fit.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fit.classList.add('is-attention');
        setTimeout(() => fit.classList.remove('is-attention'), 1600);
        setTimeout(() => fit.querySelector('select:not([disabled])')?.focus({ preventScroll: true }), 500);
        return;
      }
      nudge.hidden = true;
      const error = this.querySelector('[data-dg-atc-error]');
      error.hidden = true;
      const properties = {};
      if (this.vehicleInput && !this.vehicleInput.disabled && this.vehicleInput.value) {
        properties[this.vehicleInput.name.replace(/^properties\[|\]$/g, '')] = this.vehicleInput.value;
      }
      // Private property (hidden from the shopper, kept on the order): the delivery range quoted at add time.
      if (core()?.delivery) properties['_Delivery estimate'] = `${core().delivery.text(new Date(), '')} (quoted ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
      const cart = await whenCart();
      if (!cart) { this.form.submit(); return; } // no module support: classic post
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');
      try {
        await cart.add([{ id: this.variant.id, quantity: 1, properties }], this);
        const label = this.atc.querySelector('[data-dg-atc-label]');
        label.textContent = 'Added ✓';
        setTimeout(() => { label.textContent = this.atcLabel; }, 2000);
      } catch (err) {
        error.textContent = err.message || 'Couldn’t add to cart. Please try again.';
        error.hidden = false;
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
      }
    }

    /* ---------- sticky bar (mobile) ---------- */
    setupSticky() {
      const bar = this.querySelector('[data-dg-sticky]');
      if (!bar || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', past);
        bar.setAttribute('aria-hidden', String(!past));
        bar.querySelector('button').tabIndex = past ? 0 : -1;
      });
      this.observer.observe(this.atc);
    }
  }
  customElements.get('dg-product') || customElements.define('dg-product', DoorGlowProduct);
})();
